import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';

import { scribeLayoutTourRelaunchRequestedAtom } from '@heidi/session/data';
import { useIsSessionLayoutManagedReleaseEnabled } from '@heidi/session/flags';

import { trackSessionLayoutSwitch } from '@/models/analytics/features/track-session-layout-switch';
import {
  LABS_FEATURE,
  useIsAutoEnrolledSessionLayoutV2,
  useLabsFeatureOptIn,
} from './labs-overrides';
import { SESSION_LAYOUT_V2_RELEASE_FLAGS } from './registry';
import { useSetLabsFeatureOptIn } from './use-set-labs-feature-opt-in';

// Live release gates expose a switch only when it can change the layout.
// Managed release allows explicit opt-outs from auto-enrolment on both surfaces.
// Remove this manual override when agent-launched sessions default to V2.
export const useSessionLayoutV2MenuToggle = () => {
  const isNewSessionListEnabled = useBooleanFlagValue(
    SESSION_LAYOUT_V2_RELEASE_FLAGS[0],
    false
  );
  const isV2ReleaseEnabled = useBooleanFlagValue(
    SESSION_LAYOUT_V2_RELEASE_FLAGS[1],
    false
  );
  const isManagedReleaseEnabled = useIsSessionLayoutManagedReleaseEnabled();
  const isAutoEnrolled = useIsAutoEnrolledSessionLayoutV2();
  const optIn = useLabsFeatureOptIn(LABS_FEATURE.sessionLayoutV2);
  const requestTourRelaunch = useSetAtom(scribeLayoutTourRelaunchRequestedAtom);
  // The cohort the user was in when they flipped the toggle, not after persistence.
  const wasManagedReleaseOnSwitchRef = useRef(false);
  const { setOptIn, isPending } = useSetLabsFeatureOptIn({
    // Mutation-level tracking survives the menu (or the legacy session layout)
    // unmounting while the opt-in request is still in flight.
    onOptInPersisted: (overrides) => {
      const enabled = overrides[LABS_FEATURE.sessionLayoutV2];
      if (enabled === undefined) return;
      trackSessionLayoutSwitch(
        enabled ? 'account_menu_enabled' : 'account_menu_disabled',
        { isManagedReleaseEnabled: wasManagedReleaseOnSwitchRef.current }
      );
      if (enabled) requestTourRelaunch(true);
    },
  });

  const setEnabled = useCallback(
    (enabled: boolean) => {
      wasManagedReleaseOnSwitchRef.current = isManagedReleaseEnabled;
      setOptIn(LABS_FEATURE.sessionLayoutV2, enabled);
    },
    [isManagedReleaseEnabled, setOptIn]
  );

  return {
    isVisible:
      isManagedReleaseEnabled ||
      (isV2ReleaseEnabled && isNewSessionListEnabled && !isAutoEnrolled),
    isEnabled: isManagedReleaseEnabled ? (optIn ?? isAutoEnrolled) : optIn === true,
    isPending,
    setEnabled,
  };
};
